import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  CircularProgress,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import api from '@/services/ApiService';
import proxy from '@/services/ProxyService';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';
import EmergencyOutlinedIcon from '@mui/icons-material/EmergencyOutlined';
import HomeIcon from '@mui/icons-material/Home';
import RvHookupOutlinedIcon from '@mui/icons-material/RvHookupOutlined';
import PhoneEnabledOutlinedIcon from '@mui/icons-material/PhoneEnabledOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useRouteLoaderData } from 'react-router-dom';

const LOCATION_ICONS = {
  OFFICE: <HomeIcon fontSize="small" color="action" />,
  CUSTOMER: <RvHookupOutlinedIcon fontSize="small" color="action" />,
  REMOTE: <PhoneEnabledOutlinedIcon fontSize="small" color="action" />,
};

const LOCATION_LABELS = {
  OFFICE: 'Büro',
  CUSTOMER: 'Kunde',
  REMOTE: 'Remote',
};

const computeOtStatus = (
  startDate,
  endDate,
  config,
  startBreakDate,
  endBreakDate,
) => {
  const start = dayjs(startDate);
  let end = dayjs(endDate);
  if (end.isBefore(start)) end = end.add(1, 'day');

  const isWeekend = start.day() === 0 || start.day() === 6;
  const currentDurationMin = end.diff(start, 'minute');
  const minOvertimeMin = Number(
    config?.minOvertimeMin ?? config?.minOvertimeMinutes ?? 15,
  );

  let breakOverlapMin = 0;
  if (startBreakDate && endBreakDate) {
    let bS = dayjs(startBreakDate);
    let bE = dayjs(endBreakDate);
    if (bE.isBefore(bS)) bE = bE.add(1, 'day');
    const overlapMs = Math.max(
      0,
      Math.min(end.valueOf(), bE.valueOf()) -
        Math.max(start.valueOf(), bS.valueOf()),
    );
    breakOverlapMin = Math.round(overlapMs / 60000);
  }

  const effectiveDurationMin = Math.max(
    0,
    currentDurationMin - breakOverlapMin,
  );

  if (isWeekend) {
    return {
      possible: effectiveDurationMin >= minOvertimeMin,
      isWeekend: true,
      type: 'weekend',
      potentialOtMinutes: effectiveDurationMin,
    };
  }

  const bizStartStr = config?.businessHours?.startTime || '07:00';
  const bizEndStr = config?.businessHours?.endTime || '18:00';
  const bizStart = start
    .clone()
    .hour(parseInt(bizStartStr.split(':')[0]))
    .minute(parseInt(bizStartStr.split(':')[1]));
  const bizEnd = start
    .clone()
    .hour(parseInt(bizEndStr.split(':')[0]))
    .minute(parseInt(bizEndStr.split(':')[1]));

  const overtimeBeforeMs = Math.max(
    0,
    Math.min(bizStart.valueOf(), end.valueOf()) - start.valueOf(),
  );
  const overtimeAfterMs = Math.max(
    0,
    end.valueOf() - Math.max(bizEnd.valueOf(), start.valueOf()),
  );

  let breakOverlapInOt = 0;
  if (startBreakDate && endBreakDate) {
    let bS = dayjs(startBreakDate);
    let bE = dayjs(endBreakDate);
    if (bE.isBefore(bS)) bE = bE.add(1, 'day');

    if (overtimeBeforeMs > 0) {
      const otBeforeEndMs = Math.min(bizStart.valueOf(), end.valueOf());
      const overlapMs = Math.max(
        0,
        Math.min(otBeforeEndMs, bE.valueOf()) -
          Math.max(start.valueOf(), bS.valueOf()),
      );
      breakOverlapInOt += Math.round(overlapMs / 60000);
    }

    if (overtimeAfterMs > 0) {
      const otAfterStartMs = Math.max(bizEnd.valueOf(), start.valueOf());
      const overlapMs = Math.max(
        0,
        Math.min(end.valueOf(), bE.valueOf()) -
          Math.max(otAfterStartMs, bS.valueOf()),
      );
      breakOverlapInOt += Math.round(overlapMs / 60000);
    }
  }

  const potentialOtMinutes =
    Math.round(overtimeBeforeMs / 60000) +
    Math.round(overtimeAfterMs / 60000) -
    breakOverlapInOt;

  return {
    possible: potentialOtMinutes >= minOvertimeMin,
    isWeekend: false,
    type: 'weekday',
    potentialOtMinutes,
  };
};

export default function TanssImportDialog({ open, onClose, config }) {
  const { me, users } = useRouteLoaderData('auth');

  const [fromDate, setFromDate] = useState(dayjs().startOf('day'));
  const [toDate, setToDate] = useState(dayjs().endOf('day'));
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    current: 0,
    total: 0,
  });

  const orgTeamId = useMemo(() => {
    return (
      me?.teams?.find(
        (t) =>
          t?.type === 'ORG_UNIT' &&
          (t?.members?.find((id) => id === me?._id) ||
            t?.managers?.find((id) => id === me?._id)),
      )?._id || null
    );
  }, [me]);

  const orgUnitHRTeamId = config?.orgUnitHRTeamId || null;

  const handleLoad = useCallback(async () => {
    if (!config?.iface) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="TANSS-Interface nicht konfiguriert."
        />,
      );
      return;
    }

    setLoading(true);
    setItems([]);

    try {
      const { data: supportsData } = await proxy.put(
        `${config.iface}/supports/list`,
        {
          timeframe: {
            from: fromDate.startOf('day').unix(),
            to: toDate.endOf('day').unix(),
          },
          supportLocations: ['OFFICE', 'CUSTOMER', 'REMOTE'],
          planningTypes: ['SUPPORT'],
          employees: [me?.tanssEmployeeId].filter(Boolean),
        },
      );
      const supports = supportsData?.content || [];

      if (supports.length === 0) {
        toast.info(
          <ToastMessage
            title="Keine Ergebnisse"
            subtitle="Keine Leistungen im gewählten Zeitraum gefunden."
          />,
        );
        setLoading(false);
        return;
      }

      const fromISO = fromDate.startOf('day').toISOString();
      const toISO = toDate.endOf('day').toISOString();

      const {
        data: { result: events = [] },
      } = await api.get(
        `/event?start[$gte]=${fromISO}&end[$lte]=${toISO}&limit=10000`,
      );

      const eventBySupportId = {};
      for (const event of events) {
        for (const link of event.links || []) {
          if (link.tanssSupportId) {
            eventBySupportId[Number(link.tanssSupportId)] = event;
          }
        }
      }

      const {
        data: { result: timeRecords = [] },
      } = await api.get(
        `/timeRecord?startDate[$gte]=${fromISO}&startDate[$lte]=${toISO}&limit=10000`,
      );
      const linkedEventIdsWithOT = new Set();
      const linkedEventIdsWithEM = new Set();
      for (const tr of timeRecords) {
        if (tr.linkedEvent) {
          if (tr.isOvertime) linkedEventIdsWithOT.add(tr.linkedEvent);
          if (tr.isEmergencyService) linkedEventIdsWithEM.add(tr.linkedEvent);
        }
      }

      const uniqueCompanyIds = [
        ...new Set(supports.map((s) => s.companyId).filter(Boolean)),
      ];
      const companyMap = {};
      await Promise.allSettled(
        uniqueCompanyIds.map(async (companyId) => {
          try {
            const { data } = await proxy.get(
              `${config.iface}/companies/${companyId}`,
            );
            companyMap[companyId] = data?.content?.name || `#${companyId}`;
          } catch {
            companyMap[companyId] = `#${companyId}`;
          }
        }),
      );

      const enriched = supports.map((support) => {
        const event = eventBySupportId[Number(support.id)];
        const hasOvertimeRecord = event
          ? linkedEventIdsWithOT.has(event._id)
          : false;
        const hasEmergencyRecord = event
          ? linkedEventIdsWithEM.has(event._id)
          : false;

        const isCustomerTravel =
          support.location === 'CUSTOMER' && support.driveCharged;
        const approachMin = support.durationApproach || 0;
        const departureMin = support.durationDeparture || 0;

        const startMs = (support.date - approachMin * 60) * 1000;
        const totalDurationMin =
          (support.duration || 0) + approachMin + departureMin;
        const durationMs = totalDurationMin * 60 * 1000;
        const startDate = new Date(startMs);
        const endDate = new Date(startMs + durationMs);

        const startBreakDate = support.startBreak
          ? new Date(support.startBreak * 1000)
          : null;
        const endBreakDate =
          support.startBreak && support.durationBreak
            ? new Date(
                support.startBreak * 1000 + support.durationBreak * 60 * 1000,
              )
            : null;

        let assignmentType = null;
        if (support.linkTypeId && config?.tanssAssignmentMapping) {
          const types = Object.entries(config.tanssAssignmentMapping)
            .filter(([, v]) => v === support.linkTypeId)
            .map(([k]) => k);
          assignmentType = types[0] || null;
        }

        const employeeUser = support.employeeId
          ? users.find(
              (u) => Number(u.tanssEmployeeId) === Number(support.employeeId),
            )
          : null;

        const otStatus = computeOtStatus(
          startDate,
          endDate,
          config,
          startBreakDate,
          endBreakDate,
        );

        return {
          ...support,
          companyName: companyMap[support.companyId] || '',
          event,
          hasOvertimeRecord,
          hasEmergencyRecord,
          startDate,
          endDate,
          startBreakDate,
          endBreakDate,
          assignmentType,
          employeeUser,
          totalDurationMin,
          approachMin,
          departureMin,
          isCustomerTravel,
          otStatus,
          isOvertimeActive:
            otStatus.possible && !hasOvertimeRecord && !hasEmergencyRecord,
          isEmergencyService: false,
        };
      });

      setItems(enriched);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Leistungen konnten nicht geladen werden."
        />,
      );
    } finally {
      setLoading(false);
    }
  }, [config, fromDate, toDate, users]);

  const handleToggle = useCallback((supportId, field) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== supportId) return item;
        const newValue = !item[field];
        const updates = { [field]: newValue };

        if (field === 'isOvertimeActive' && newValue) {
          updates.isEmergencyService = false;
        }
        if (field === 'isEmergencyService' && newValue) {
          updates.isOvertimeActive = false;
        }

        return { ...item, ...updates };
      }),
    );
  }, []);

  const selectedItems = useMemo(() => {
    return items.filter(
      (item) =>
        (item.isOvertimeActive || item.isEmergencyService) &&
        item.event &&
        !(item.isOvertimeActive && item.hasOvertimeRecord) &&
        !(item.isEmergencyService && item.hasEmergencyRecord),
    );
  }, [items]);

  const handleImport = async () => {
    if (selectedItems.length === 0) return;
    if (!orgUnitHRTeamId || !orgTeamId) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Organisationsstruktur nicht konfiguriert (orgUnitHRTeamId / orgTeamId)."
        />,
      );
      return;
    }

    setImporting(true);
    setImportProgress({ current: 0, total: selectedItems.length });

    let success = 0;
    let failed = 0;

    const maxH = config?.maxWeekendHours || 8;
    const biz = config?.businessHours || {
      startTime: '07:00',
      endTime: '18:00',
    };

    for (let i = 0; i < selectedItems.length; i++) {
      const item = selectedItems[i];
      setImportProgress({ current: i + 1, total: selectedItems.length });

      try {
        const eventId = item.event._id;
        const userId = item.employeeUser?._id || me._id;
        const isWE = item.otStatus.isWeekend;

        let partyId = item.event?.partyId || null;
        if (!partyId && item.companyId) {
          try {
            const {
              data: { result },
            } = await api.get(
              `/datasource?sourceId=${Number(item.companyId)}&source=tanss`,
            );
            partyId = result[0]?.recordId || null;
          } catch {
            /* ignore */
          }
        }

        if (item.isOvertimeActive) {
          const bS = dayjs(item.startDate);
          let bE = dayjs(item.endDate);
          if (bE.isBefore(bS)) bE = bE.add(1, 'day');

          let otS, otE;

          if (isWE) {
            const date = bS;
            const startRange = (
              date.day() === 0 ? date.subtract(1, 'day') : date
            )
              .startOf('day')
              .toISOString();
            const endRange = (date.day() === 6 ? date.add(1, 'day') : date)
              .endOf('day')
              .toISOString();
            const res = await api.get(
              `/timeRecord?user=${userId}&startDate[$gte]=${startRange}&startDate[$lte]=${endRange}`,
            );
            const accSeconds = (res.data?.result || []).reduce(
              (sum, r) => sum + (r.duration || 0),
              0,
            );
            const avail = Math.max(0, maxH * 3600 - accSeconds);
            if (avail > 0) {
              otS = bS;
              otE = bS.add(Math.min(bE.diff(bS, 'second'), avail), 'second');
            }
          } else {
            const bizStart = bS
              .clone()
              .hour(parseInt(biz.startTime.split(':')[0]))
              .minute(parseInt(biz.startTime.split(':')[1]));
            const bizEnd = bS
              .clone()
              .hour(parseInt(biz.endTime.split(':')[0]))
              .minute(parseInt(biz.endTime.split(':')[1]));
            if (bE.isAfter(bizEnd)) {
              otS = bS.isAfter(bizEnd) ? bS : bizEnd;
              otE = bE;
            } else if (bS.isBefore(bizStart)) {
              otS = bS;
              otE = bE.isAfter(bizStart) ? bizStart : bE;
            }
          }

          if (otS && otE?.diff(otS) > 0) {
            let breakSec = 0;
            if (item.startBreakDate && item.endBreakDate) {
              const bs = dayjs(item.startBreakDate);
              const be = dayjs(item.endBreakDate);
              const overlapMs = Math.max(
                0,
                Math.min(otE.valueOf(), be.valueOf()) -
                  Math.max(otS.valueOf(), bs.valueOf()),
              );
              breakSec = Math.round(overlapMs / 1000);
            }

            const effectiveSec = Math.max(
              0,
              otE.diff(otS, 'second') - breakSec,
            );
            if (effectiveSec > 0) {
              const otPayload = {
                title: `Überstunde: ${item.text || ''}`,
                description: item.text || '',
                startDate: otS.toISOString(),
                endDate: otE.toISOString(),
                duration: effectiveSec,
                isOvertime: true,
                isEmergencyService: false,
                isWeekend: isWE,
                user: userId,
                customerId: String(item.companyId),
                ticketReference: item.ticketId
                  ? `#${Number(item.ticketId)}`
                  : undefined,
                linkedEvent: eventId,
                sharedWith: [
                  {
                    id: orgUnitHRTeamId,
                    type: 'team',
                    roles: 'all',
                    permissions: {
                      read: true,
                      update: true,
                      delete: true,
                    },
                  },
                  {
                    id: orgTeamId,
                    type: 'team',
                    roles: 'manager',
                    permissions: {
                      read: true,
                      update: true,
                      delete: true,
                    },
                  },
                ],
              };
              if (partyId) otPayload.partyId = partyId;
              await api.post('/timeRecord', otPayload);
              success++;
            }
          }
        }

        if (item.isEmergencyService) {
          const bS = dayjs(item.startDate);
          let bE = dayjs(item.endDate);
          if (bE.isBefore(bS)) bE = bE.add(1, 'day');

          let durationSec = bE.diff(bS, 'second');
          if (item.startBreakDate && item.endBreakDate) {
            const bs = dayjs(item.startBreakDate);
            const be = dayjs(item.endBreakDate);
            const overlapMs = Math.max(
              0,
              Math.min(bE.valueOf(), be.valueOf()) -
                Math.max(bS.valueOf(), bs.valueOf()),
            );
            durationSec = Math.max(
              0,
              durationSec - Math.round(overlapMs / 1000),
            );
          }

          const emPayload = {
            title: `Notdienst: ${item.text || ''}`,
            description: item.text || '',
            startDate: bS.toISOString(),
            endDate: bE.toISOString(),
            duration: durationSec,
            isOvertime: false,
            isEmergencyService: true,
            isWeekend: isWE,
            user: userId,
            customerId: String(item.companyId),
            ticketReference: item.ticketId
              ? `#${Number(item.ticketId)}`
              : undefined,
            linkedEvent: eventId,
            sharedWith: [
              {
                id: orgUnitHRTeamId,
                type: 'team',
                roles: 'all',
                permissions: { read: true, update: true, delete: true },
              },
              {
                id: orgTeamId,
                type: 'team',
                roles: 'manager',
                permissions: { read: true, update: true, delete: true },
              },
            ],
          };
          if (partyId) emPayload.partyId = partyId;
          await api.post('/timeRecord', emPayload);
          success++;
        }
      } catch (error) {
        console.error('Import-Fehler für Support', item.id, error);
        failed++;
      }
    }

    setImporting(false);

    if (success > 0) {
      toast.success(
        <ToastMessage
          title="Import abgeschlossen"
          subtitle={`${success} Zeiteinträge erstellt${failed > 0 ? `, ${failed} fehlgeschlagen` : ''}.`}
        />,
      );
    }
    if (failed > 0 && success === 0) {
      toast.error(
        <ToastMessage
          title="Import fehlgeschlagen"
          subtitle={`${failed} Einträge konnten nicht importiert werden.`}
        />,
      );
    }

    setItems((prev) =>
      prev.map((item) => {
        const imported = selectedItems.find((s) => s.id === item.id);
        if (!imported) return item;
        return {
          ...item,
          hasOvertimeRecord:
            item.hasOvertimeRecord || imported.isOvertimeActive,
          hasEmergencyRecord:
            item.hasEmergencyRecord || imported.isEmergencyService,
          isOvertimeActive: false,
          isEmergencyService: false,
        };
      }),
    );
  };

  return (
    <Dialog
      open={open}
      onClose={importing ? undefined : onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Leistungen importieren
          <Box sx={{ flexGrow: 1 }} />
          {items.length > 0 && (
            <Chip
              label={`${items.length} Leistungen`}
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pt: '10px !important',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <DatePicker
            label="Von"
            value={fromDate}
            onChange={setFromDate}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="Bis"
            value={toDate}
            onChange={setToDate}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            onClick={handleLoad}
            disabled={loading || importing}
            sx={{
              height: '39px',
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'Laden'}
          </Button>
        </Box>

        {importing && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Importiere {importProgress.current} / {importProgress.total}...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                importProgress.total > 0
                  ? (importProgress.current / importProgress.total) * 100
                  : 0
              }
            />
          </Box>
        )}

        {/* Header row */}
        {items.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 0.5,
              px: 1,
              borderBottom: '2px solid',
              borderColor: 'divider',
              mt: 1,
            }}
          >
            <Typography variant="caption" sx={{ width: 28 }} />
            <Typography
              variant="caption"
              sx={{ minWidth: 130, fontWeight: 600 }}
            >
              Kunde
            </Typography>
            <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
              Beschreibung
            </Typography>
            <Typography
              variant="caption"
              sx={{ minWidth: 60, fontWeight: 600 }}
            >
              Ticket
            </Typography>
            <Typography
              variant="caption"
              sx={{ minWidth: 150, fontWeight: 600 }}
            >
              Datum / Zeit
            </Typography>
            <Typography
              variant="caption"
              sx={{ minWidth: 80, fontWeight: 600 }}
            >
              Dauer
            </Typography>
            <Typography
              variant="caption"
              sx={{ minWidth: 30, fontWeight: 600 }}
            >
              %
            </Typography>
            <Typography
              variant="caption"
              sx={{ minWidth: 100, fontWeight: 600, textAlign: 'center' }}
            >
              Status
            </Typography>
            <Typography
              variant="caption"
              sx={{ width: 36, fontWeight: 600, textAlign: 'center' }}
            >
              ÜS
            </Typography>
            <Typography
              variant="caption"
              sx={{ width: 36, fontWeight: 600, textAlign: 'center' }}
            >
              ND
            </Typography>
          </Box>
        )}

        {/* Scrollable list */}
        <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
          {items.map((item) => {
            const alreadyImported =
              item.hasOvertimeRecord || item.hasEmergencyRecord;
            const isDisabled = !item.event || alreadyImported;
            const startStr = dayjs(item.startDate).format('DD.MM.YY HH:mm');
            const endStr = dayjs(item.endDate).format('HH:mm');
            const displayMin = item.totalDurationMin ?? item.duration ?? 0;
            const durationH = Math.floor(displayMin / 60);
            const durationM = displayMin % 60;
            const durationStr = `${durationH ? `${durationH}h ` : ''}${durationM}m`;

            const employeeName = item.employeeUser
              ? `${item.employeeUser.firstName || ''} ${item.employeeUser.lastName || ''}`.trim()
              : `MA #${item.employeeId || '?'}`;

            const otDisabled =
              isDisabled || !item.otStatus.possible || item.isEmergencyService;
            const emDisabled = isDisabled || item.isOvertimeActive;

            return (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 0.75,
                  px: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  opacity: isDisabled ? 0.45 : 1,
                  bgcolor:
                    item.hasOvertimeRecord && item.hasEmergencyRecord
                      ? 'action.selected'
                      : item.hasOvertimeRecord || item.hasEmergencyRecord
                        ? 'action.hover'
                        : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {/* Location icon */}
                <Tooltip
                  title={LOCATION_LABELS[item.location] || item.location || '—'}
                  arrow
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', width: 28 }}
                  >
                    {LOCATION_ICONS[item.location] || (
                      <HomeIcon fontSize="small" color="action" />
                    )}
                  </Box>
                </Tooltip>

                {/* Employee
                <Tooltip title={employeeName} arrow>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 120, fontWeight: 500 }}
                    noWrap
                  >
                    {employeeName}
                  </Typography>
                </Tooltip> */}

                {/* Company */}
                <Tooltip title={item.companyName || '—'} arrow>
                  <Typography variant="body2" sx={{ minWidth: 130 }} noWrap>
                    {item.companyName || '—'}
                  </Typography>
                </Tooltip>

                {/* Description */}
                <Tooltip title={item.text || item.textIntern || ''} arrow>
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, minWidth: 80 }}
                    noWrap
                  >
                    {item.text || '—'}
                  </Typography>
                </Tooltip>

                {/* Ticket */}
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 60,
                    color: item.ticketId ? 'text.primary' : 'text.secondary',
                  }}
                  noWrap
                >
                  {item.ticketId ? `#${item.ticketId}` : '—'}
                </Typography>

                {/* Date/Time */}
                <Typography variant="body2" sx={{ minWidth: 150 }} noWrap>
                  {startStr} – {endStr}
                </Typography>

                {/* Duration */}
                <Tooltip
                  title={
                    item.isCustomerTravel
                      ? `Anfahrt: ${item.approachMin}min, Abfahrt: ${item.departureMin}min (in Dauer enthalten)`
                      : ''
                  }
                  arrow
                  disableHoverListener={!item.isCustomerTravel}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: 80,
                      color: item.isCustomerTravel
                        ? 'info.main'
                        : 'text.primary',
                    }}
                    noWrap
                  >
                    {durationStr}
                    {item.isCustomerTravel ? '*' : ''}
                  </Typography>
                </Tooltip>

                {/* Percent */}
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 30,
                    color: item.percent ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {item.percent != null ? `${item.percent}%` : '—'}
                </Typography>

                {/* Status */}
                <Box
                  sx={{
                    minWidth: 100,
                    display: 'flex',
                    gap: 0.5,
                    justifyContent: 'center',
                  }}
                >
                  {item.hasOvertimeRecord && (
                    <Chip
                      label="ÜS"
                      size="small"
                      color="success"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {item.hasEmergencyRecord && (
                    <Chip
                      label="ND"
                      size="small"
                      color="error"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {!item.event && (
                    <Tooltip
                      title="Kein verknüpftes Event in Codemeta gefunden"
                      arrow
                    >
                      <WarningAmberIcon fontSize="small" color="warning" />
                    </Tooltip>
                  )}
                  {item.event &&
                    !item.hasOvertimeRecord &&
                    !item.hasEmergencyRecord && (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                </Box>

                {/* Overtime toggle */}
                <Tooltip
                  title={
                    item.hasOvertimeRecord
                      ? 'Überstunde bereits vorhanden'
                      : !item.event
                        ? 'Kein verknüpftes Event'
                        : item.isEmergencyService
                          ? 'Deaktiviert — Notdienst aktiv'
                          : !item.otStatus.possible
                            ? 'Keine Überstunden möglich'
                            : item.isOvertimeActive
                              ? 'Überstunde wird erfasst'
                              : 'Überstunde aktivieren'
                  }
                  arrow
                >
                  <span>
                    <ToggleButtonGroup
                      size="small"
                      color="primary"
                      value={item.isOvertimeActive ? 'active' : ''}
                      exclusive
                      onChange={() => {
                        if (!otDisabled) {
                          handleToggle(item.id, 'isOvertimeActive');
                        }
                      }}
                      sx={
                        otDisabled
                          ? { pointerEvents: 'none', opacity: 0.4 }
                          : {}
                      }
                    >
                      <ToggleButton
                        value="active"
                        sx={{ width: 36, height: 30, p: 0 }}
                      >
                        {item.isOvertimeActive ? (
                          <TimerOutlinedIcon fontSize="small" />
                        ) : (
                          <TimerOffOutlinedIcon fontSize="small" />
                        )}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </span>
                </Tooltip>

                {/* Emergency toggle */}
                <Tooltip
                  title={
                    item.hasEmergencyRecord
                      ? 'Notdienst bereits vorhanden'
                      : !item.event
                        ? 'Kein verknüpftes Event'
                        : item.isOvertimeActive
                          ? 'Deaktiviert — Überstunden aktiv'
                          : item.isEmergencyService
                            ? 'Wird als Notdienst erfasst'
                            : 'Notdienst aktivieren'
                  }
                  arrow
                >
                  <span>
                    <ToggleButtonGroup
                      size="small"
                      color="error"
                      value={item.isEmergencyService ? 'active' : ''}
                      exclusive
                      onChange={() => {
                        if (!emDisabled) {
                          handleToggle(item.id, 'isEmergencyService');
                        }
                      }}
                      sx={
                        emDisabled
                          ? { pointerEvents: 'none', opacity: 0.4 }
                          : {}
                      }
                    >
                      <ToggleButton
                        value="active"
                        sx={{ width: 36, height: 30, p: 0 }}
                      >
                        <EmergencyOutlinedIcon fontSize="small" />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </span>
                </Tooltip>
              </Box>
            );
          })}

          {items.length === 0 && !loading && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 4, textAlign: 'center' }}
            >
              Keine Leistungen geladen. Wählen Sie einen Zeitraum und klicken
              Sie auf &quot;Laden&quot;.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          {selectedItems.length > 0 &&
            `${selectedItems.length} Einträge ausgewählt`}
        </Typography>
        <Button onClick={onClose} color="inherit" disabled={importing}>
          Schließen
        </Button>
        <Button
          variant="outlined"
          onClick={handleImport}
          disabled={selectedItems.length === 0 || importing}
        >
          {importing
            ? `Importiere... (${importProgress.current}/${importProgress.total})`
            : `Importieren (${selectedItems.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
